'use client';

import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { cn } from 'utils/cn';
import { useOverlayScope } from 'components/ui/overlay-motion';

import { Button } from 'components/ui/button';
import { XIcon } from 'lucide-react';

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ className, ...props }: DialogPrimitive.Trigger.Props) {
    return (
        <DialogPrimitive.Trigger data-slot="dialog-trigger" className={cn('cursor-pointer', className)} {...props} />
    );
}

function DialogPortal({ container, ...props }: DialogPrimitive.Portal.Props) {
    const scope = useOverlayScope();
    return (
        <DialogPrimitive.Portal
            data-slot="dialog-portal"
            container={container === undefined ? scope.container : container}
            {...props}
        />
    );
}

function DialogClose({ className, ...props }: DialogPrimitive.Close.Props) {
    return <DialogPrimitive.Close data-slot="dialog-close" className={cn('cursor-pointer', className)} {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
    return (
        <DialogPrimitive.Backdrop
            data-slot="dialog-overlay"
            className={cn(
                'data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 new:bg-overlay new:backdrop-blur-none fixed inset-0 isolate z-(--z-modal) bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs motion-reduce:animate-none!',
                className
            )}
            {...props}
        />
    );
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    container,
    keepMounted,
    overlayProps,
    ...props
}: DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean;
    /** Where the dialog is portalled; an AI surface's container or `<body>` unless given. */
    container?: DialogPrimitive.Portal.Props['container'];
    /** Keeps the content mounted, hidden, while the dialog is closed; unmounted then unless given. */
    keepMounted?: DialogPrimitive.Portal.Props['keepMounted'];
    overlayProps?: DialogPrimitive.Backdrop.Props;
}) {
    return (
        <DialogPortal container={container} keepMounted={keepMounted}>
            <DialogOverlay {...overlayProps} />
            <DialogPrimitive.Popup
                data-slot="dialog-content"
                className={cn(
                    'bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 new:shadow-dialog new:outline-hidden fixed top-1/2 left-1/2 z-(--z-modal) grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 rounded-xl p-6 text-sm ring-1 duration-100 outline-none motion-reduce:animate-none! sm:max-w-md',
                    className
                )}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        render={<Button variant="ghost" className="absolute top-4 right-4" size="icon-sm" />}
                    >
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Popup>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />;
}

function DialogFooter({
    className,
    showCloseButton = false,
    children,
    ...props
}: React.ComponentProps<'div'> & {
    showCloseButton?: boolean;
}) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
            {...props}
        >
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
            )}
        </div>
    );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn('leading-none font-medium', className)}
            {...props}
        />
    );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn(
                'text-muted-foreground *:[a]:hover:text-foreground new:*:[a]:underline-offset-auto text-sm *:[a]:underline *:[a]:underline-offset-3',
                className
            )}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
